import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/UI';
import { trpc } from '@/api/trpc';
import type { StrategyContext } from './types';

interface BaselineExperimentCardProps {
    strategyContext: StrategyContext | null;
    isOwnedScorecard: boolean;
    canEditScorecard: boolean;
    baselineExperimentId: number | null;
    setBaselineExperimentId: React.Dispatch<React.SetStateAction<number | null>>;
}

const BaselineExperimentCard = ({
    strategyContext,
    isOwnedScorecard,
    canEditScorecard,
    baselineExperimentId,
    setBaselineExperimentId
}: BaselineExperimentCardProps) => {
    // Completed experiments for baseline selection (filter by strategy)
    const {
        data: experiments,
        isLoading: experimentsLoading,
        error: experimentsError
    } = useQuery({
        ...trpc.experiment.list.queryOptions({ executionStatus: 'completed', limit: 100, offset: 0 }),
        select: (resp) =>
            resp.experiments.filter((exp) => {
                const sid = strategyContext?.strategyId;
                return !!sid && exp.strategyId === sid;
            }),
        enabled: isOwnedScorecard && !!strategyContext?.strategyId
    });

    return (
        <Card className="p-4">
            <h3 className="text-white text-lg font-semibold mb-4">Baseline Experiment</h3>
            {experimentsLoading ? (
                <div className="text-zinc-400 text-sm">Loading experiments…</div>
            ) : experimentsError ? (
                <div className="text-red-400 text-sm">Failed to load experiments: {experimentsError.message}</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-zinc-300" htmlFor="baseline-experiment-select">
                            Select Baseline Experiment (required)
                        </label>
                        <select
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-zinc-100"
                            disabled={!canEditScorecard}
                            id="baseline-experiment-select"
                            value={baselineExperimentId != null ? String(baselineExperimentId) : ''}
                            onChange={(e) => setBaselineExperimentId(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">Select baseline experiment…</option>
                            {(experiments || []).map((exp) => (
                                <option key={exp.id} value={exp.id}>
                                    #{exp.id} - {exp.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="text-xs text-zinc-400 flex items-center">
                        Baseline extraction will use this experiment&apos;s results for entity baselines.
                    </div>
                </div>
            )}
        </Card>
    );
};

export default BaselineExperimentCard;
